/**
 * O efeito de um relatório no contrato, antes de lançá-lo.
 *
 * O formulário do relatório mostra, enquanto a pessoa preenche, o que vai
 * mudar: o valor pago, o valor do contrato, o saldo, o fim do prazo. A conta é
 * a do próprio razão (`applyMovement`), a mesma que o servidor faz ao publicar —
 * a prévia não pode prometer um número que a publicação não daria, nem deixar
 * passar o valor que o servidor vai recusar.
 */

import { REPORT_TYPE_META, type ReportType } from './domain.js';
import {
  daysBetween,
  formatCurrency,
  formatCurrencyDelta,
  formatDate,
  formatDays,
} from './format.js';
import {
  addDays,
  applyMovement,
  coversCommitted,
  LedgerError,
  remainingBalanceCents,
  type ObraLedgerState,
} from './ledger.js';

/** Uma linha do efeito: o que era, o que passa a ser e, quando cabe, quanto mudou. */
export interface LinhaDoEfeito {
  readonly rotulo: string;
  readonly antes: string;
  readonly depois: string;
  readonly variacao?: string | undefined;
}

export type PrevisaoDoEfeito =
  /** Visita: não mexe em valor nem em prazo. */
  | { readonly situacao: 'sem_efeito' }
  /** Ainda falta o valor ou os dias para haver o que mostrar. */
  | { readonly situacao: 'incompleta'; readonly campo: 'amountCents' | 'days' }
  /** A regra do razão recusa: o servidor recusaria igual. */
  | {
      readonly situacao: 'recusada';
      readonly campo: 'amountCents' | 'days';
      readonly mensagem: string;
    }
  | {
      readonly situacao: 'calculada';
      readonly linhas: readonly LinhaDoEfeito[];
      readonly nota?: string | undefined;
    };

export interface EntradaDaPrevisao {
  readonly type: ReportType;
  readonly amountCents?: number | undefined;
  readonly days?: number | undefined;
  /** Data do ato; sem ela, a conta usa uma qualquer — a data não muda o efeito. */
  readonly effectiveDate?: string | undefined;
}

/** O que falta não é erro: o formulário só pede o valor, em vez de acusá-lo. */
const FALTA: ReadonlySet<string> = new Set([
  'AMOUNT_REQUIRED',
  'AMOUNT_MUST_BE_POSITIVE',
  'DAYS_REQUIRED',
  'DAYS_MUST_BE_POSITIVE',
]);

/** Variação de prazo em texto: "+30 dias", "−5 dias". */
function variacaoDeDias(antes: string | null, depois: string | null): string | undefined {
  if (!antes || !depois) return undefined;
  const dias = daysBetween(antes, depois);
  return `${dias < 0 ? '−' : '+'}${formatDays(Math.abs(dias))}`;
}

/**
 * A prévia do efeito.
 *
 * `estado` é de onde a conta parte. Na correção (ADR 060) ela parte do estado
 * sem o lançamento que vai ser estornado (`estadoSemLancamento`), mas quem lê
 * quer comparar com o contrato como ele está: `comparadoA` é esse "antes", e a
 * variação passa a ser a diferença que a correção faz.
 */
export function preverEfeito(
  estado: ObraLedgerState,
  entrada: EntradaDaPrevisao,
  opcoes: { readonly comparadoA?: ObraLedgerState | undefined } = {},
): PrevisaoDoEfeito {
  const meta = REPORT_TYPE_META[entrada.type];
  if (meta.effect === 'none') return { situacao: 'sem_efeito' };

  let depois: ObraLedgerState;
  try {
    depois = applyMovement(estado, {
      type: entrada.type,
      amountCents: entrada.amountCents,
      days: entrada.days,
      effectiveDate: entrada.effectiveDate ?? '2000-01-01',
    }).nextState;
  } catch (erro) {
    if (!(erro instanceof LedgerError)) throw erro;
    return FALTA.has(erro.violation)
      ? { situacao: 'incompleta', campo: erro.field }
      : { situacao: 'recusada', campo: erro.field, mensagem: erro.message };
  }

  // Diminuir o contrato abaixo do que já foi pago — tirar um aditivo medido — não fecha.
  if (!coversCommitted(depois)) {
    return {
      situacao: 'recusada',
      campo: 'amountCents',
      mensagem: 'Com este valor, o contrato ficaria abaixo do que já foi pago ou glosado.',
    };
  }

  const antes = opcoes.comparadoA ?? estado;

  const dinheiro = (rotulo: string, de: number, para: number): LinhaDoEfeito => ({
    rotulo,
    antes: formatCurrency(de),
    depois: formatCurrency(para),
    variacao: formatCurrencyDelta(para - de),
  });

  const saldo: LinhaDoEfeito = {
    rotulo: 'Saldo a pagar',
    antes: formatCurrency(remainingBalanceCents(antes)),
    depois: formatCurrency(remainingBalanceCents(depois)),
  };

  switch (meta.effect) {
    case 'paid':
      return {
        situacao: 'calculada',
        linhas: [dinheiro('Valor pago', antes.paidValueCents, depois.paidValueCents), saldo],
      };

    case 'contract_value':
      return {
        situacao: 'calculada',
        linhas: [
          dinheiro('Valor do contrato', antes.currentValueCents, depois.currentValueCents),
          saldo,
        ],
      };

    case 'gloss':
      return {
        situacao: 'calculada',
        linhas: [dinheiro('Valor glosado', antes.glossValueCents, depois.glossValueCents), saldo],
        nota: 'A glosa encerra o contrato financeiramente: o saldo restante deixa de ser devido.',
      };

    case 'os_term':
      return {
        situacao: 'calculada',
        linhas: [
          {
            rotulo: 'Fim da OS',
            antes: formatDate(antes.osEnd),
            depois: formatDate(depois.osEnd),
            variacao: variacaoDeDias(antes.osEnd, depois.osEnd),
          },
        ],
      };

    case 'contract_term':
      return {
        situacao: 'calculada',
        linhas: [
          {
            rotulo: 'Fim do contrato',
            antes: formatDate(antes.contractEnd),
            depois: formatDate(depois.contractEnd),
            variacao: variacaoDeDias(antes.contractEnd, depois.contractEnd),
          },
        ],
      };
  }
}

/** O lançamento de um relatório, com o antes e o depois que ele gravou. */
export interface LancamentoGravado {
  readonly type: ReportType;
  readonly amountCents: number;
  readonly days: number | null;
  readonly previousValueCents: number;
  readonly newValueCents: number;
  readonly previousPaidCents: number;
  readonly newPaidCents: number;
}

/**
 * O estado da obra sem um lançamento — o ponto de partida da correção (ADR 060).
 *
 * Corrigir o valor estorna o lançamento que vale e lança o novo; a prévia da
 * correção, então, parte do estado que o estorno deixaria. É a conta inversa da
 * que o lançamento fez, como em `reverseMovement`, com o que a tela tem dele.
 */
export function estadoSemLancamento(
  estado: ObraLedgerState,
  lancamento: LancamentoGravado,
): ObraLedgerState {
  const dias = lancamento.days ?? 0;

  return {
    currentValueCents:
      estado.currentValueCents - (lancamento.newValueCents - lancamento.previousValueCents),
    paidValueCents:
      estado.paidValueCents - (lancamento.newPaidCents - lancamento.previousPaidCents),
    glossValueCents:
      lancamento.type === 'glosa'
        ? estado.glossValueCents - lancamento.amountCents
        : estado.glossValueCents,
    contractEnd:
      lancamento.type === 'aditivo_prazo_contrato' && estado.contractEnd && dias
        ? addDays(estado.contractEnd, -dias)
        : estado.contractEnd,
    osEnd:
      lancamento.type === 'aditivo_prazo_os' && estado.osEnd && dias
        ? addDays(estado.osEnd, -dias)
        : estado.osEnd,
  };
}
